import { istToday } from "@/lib/stayflowApi";
import React, { useState, useEffect, useMemo } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format, differenceInCalendarDays } from "date-fns";
import { Search, MapPin, CalendarDays, User, Minus, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDesktopPointer } from "@/components/stays/HomeLiveElements";
import { WhereSuggestions } from "@/components/stays/search/WhereSuggestions";

export const POPULAR = ["Guwahati", "Golaghat", "Kaziranga", "Jorhat", "Shillong", "Dibrugarh"];

const useIsDesktop = () => {
  const [d, setD] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(min-width: 768px)");
    const on = () => setD(m.matches);
    on();
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return d;
};

export const Stepper = ({ label, sub, value, setValue, min = 0, max = 30, testid }) => (
  <div className="flex items-center justify-between py-3">
    <div>
      <p className="font-heading font-semibold text-ink">{label}</p>
      {sub && <p className="text-xs text-ink-faint">{sub}</p>}
    </div>
    <div className="flex items-center gap-4">
      <button onClick={() => setValue(Math.max(min, value - 1))} disabled={value <= min} data-testid={`${testid}-dec`} className="w-9 h-9 rounded-full border border-black/15 flex items-center justify-center text-ink disabled:opacity-30 hover:border-brand transition-colors"><Minus className="w-4 h-4" /></button>
      <span className="w-6 text-center font-semibold text-ink" data-testid={`${testid}-val`}>{value}</span>
      <button onClick={() => setValue(Math.min(max, value + 1))} disabled={value >= max} data-testid={`${testid}-inc`} className="w-9 h-9 rounded-full border border-black/15 flex items-center justify-center text-ink disabled:opacity-30 hover:border-brand transition-colors"><Plus className="w-4 h-4" /></button>
    </div>
  </div>
);

const Segment = ({ label, children, className = "", testid }) => (
  <div data-testid={testid} className={`flex-1 min-w-0 text-left px-5 py-3.5 md:py-2.5 ${className}`}>
    <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">{label}</p>
    <div className="mt-0.5">{children}</div>
  </div>
);

export const SearchWidget = ({
  search, setSearch, stays = [],
  dateRange, setDateRange,
  rooms, setRooms, adults, setAdults, childrenCount, setChildren,
  onSearch, onNearMe, onPickPlace, onPickStay, locating,
}) => {
  const isDesktop = useIsDesktop();
  const desktopPtr = useDesktopPointer();
  const [spot, setSpot] = useState({ x: -1, y: -1, on: false });
  const onSpotMove = (e) => { if (!desktopPtr) return; const r = e.currentTarget.getBoundingClientRect(); setSpot({ x: e.clientX - r.left, y: e.clientY - r.top, on: true }); };
  const onSpotLeave = () => setSpot((s) => ({ ...s, on: false }));
  const today = useMemo(() => istToday(), []);
  const [showCities, setShowCities] = useState(false);

  const nights = dateRange?.from && dateRange?.to ? Math.max(differenceInCalendarDays(dateRange.to, dateRange.from), 0) : 0;
  const guestsLabel = `${adults + childrenCount} guest${adults + childrenCount !== 1 ? "s" : ""} · ${rooms} room${rooms !== 1 ? "s" : ""}`;
  const dateLabel = dateRange?.from && dateRange?.to
    ? `${format(dateRange.from, "dd MMM")} – ${format(dateRange.to, "dd MMM")}`
    : dateRange?.from ? `${format(dateRange.from, "dd MMM")} – …` : "Add dates";

  return (
    <div data-testid="search-widget" onMouseMove={onSpotMove} onMouseLeave={onSpotLeave} className="relative w-full max-w-4xl mx-auto rounded-[28px] md:rounded-full bg-white border border-black/10 shadow-[0_18px_50px_-18px_rgba(20,20,20,0.28)] flex flex-col md:flex-row md:items-center divide-y md:divide-y-0 md:divide-x divide-black/10">
      {desktopPtr && (
        <div
          className="pointer-events-none absolute inset-0 rounded-[28px] md:rounded-full z-[1] transition-opacity duration-300"
          style={{ opacity: spot.on ? 1 : 0, background: `radial-gradient(350px circle at ${spot.x}px ${spot.y}px, rgba(255,26,26,0.07), transparent 80%)` }}
          data-testid="search-spotlight"
        />
      )}
      {/* WHERE */}
      <div className="relative flex-[1.4] min-w-0">
        <Segment label="Where" testid="search-where">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-brand shrink-0" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setShowCities(true); }}
              onFocus={() => setShowCities(true)}
              onBlur={() => setTimeout(() => setShowCities(false), 150)}
              placeholder="Search destinations"
              data-testid="search-where-input"
              autoComplete="off"
              className="w-full bg-transparent outline-none font-heading font-semibold text-base text-ink placeholder:text-ink-faint placeholder:font-sans placeholder:font-normal"
            />
          </div>
        </Segment>
        {showCities && (
          <div className="absolute left-2 right-2 md:left-3 top-full mt-2 z-50 rounded-2xl bg-white border border-black/10 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.28)] overflow-hidden max-h-[60vh] overflow-y-auto">
            <WhereSuggestions
              query={search} stays={stays} locating={locating}
              onPickPlace={(p) => { setShowCities(false); setSearch(p.main); onPickPlace?.(p); }}
              onPickStay={(s) => { setShowCities(false); onPickStay?.(s); }}
              onNearMe={onNearMe ? () => { setShowCities(false); onNearMe(); } : undefined}
            />
          </div>
        )}
      </div>

      {/* WHEN */}
      <Popover>
        <PopoverTrigger asChild>
          <button className="flex-1 min-w-0 text-left px-5 py-3.5 md:py-2.5 hover:bg-paper-muted/60 md:rounded-none transition-colors" data-testid="search-when">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">When</p>
            <div className="mt-0.5 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-brand shrink-0" />
              <span className="font-heading font-semibold text-base text-ink truncate">{dateLabel}</span>
              {nights > 0 && <span className="shrink-0 text-xs text-ink-faint">· {nights}N</span>}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="center">
          <div className="rdp-wrap" style={{ "--rdp-accent-color": "#FF1A1A", "--rdp-background-color": "#FFE9E7", "--rdp-cell-size": "40px" }}>
            <DayPicker mode="range" selected={dateRange} onSelect={setDateRange} numberOfMonths={isDesktop ? 2 : 1} fromDate={today} defaultMonth={dateRange?.from || today} />
          </div>
          {dateRange?.from && (
            <button onClick={() => setDateRange(undefined)} className="w-full mt-1 text-sm text-ink-soft hover:text-brand py-1.5" data-testid="search-clear-dates">Clear dates</button>
          )}
        </PopoverContent>
      </Popover>

      {/* WHO */}
      <Popover>
        <PopoverTrigger asChild>
          <button className="flex-1 min-w-0 text-left px-5 py-3.5 md:py-2.5 hover:bg-paper-muted/60 transition-colors" data-testid="search-who">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">Who</p>
            <div className="mt-0.5 flex items-center gap-2">
              <User className="w-4 h-4 text-brand shrink-0" />
              <span className="font-heading font-semibold text-base text-ink truncate" data-testid="search-who-label">{guestsLabel}</span>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-4" align="end">
          <div className="divide-y divide-black/5">
            <Stepper label="Rooms" value={rooms} setValue={setRooms} min={1} max={20} testid="search-rooms" />
            <Stepper label="Adults" sub="Age 13+" value={adults} setValue={setAdults} min={1} max={30} testid="search-adults" />
            <Stepper label="Children" sub="Age 0–12" value={childrenCount} setValue={setChildren} min={0} max={20} testid="search-children" />
          </div>
        </PopoverContent>
      </Popover>

      {/* SEARCH BUTTON */}
      <div className="p-3 md:p-2 md:pr-2.5 relative z-[2]">
        <button onClick={onSearch} data-testid="search-submit"
          className="w-full h-12 md:h-14 md:w-14 rounded-2xl md:rounded-full bg-brand text-white font-heading font-bold text-base tracking-wide shadow-[0_10px_28px_rgba(255,26,26,0.4)] transition-all duration-300 hover:-translate-y-0.5 lg:hover:scale-105 lg:hover:shadow-[0_0_25px_rgba(255,26,26,0.40)] active:scale-95 flex items-center justify-center gap-2">
          <Search className="w-5 h-5" />
          <span className="md:hidden">Search</span>
        </button>
      </div>
    </div>
  );
};

export default SearchWidget;
